import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import { getPreviousPeriodDateRange } from '@buffer-mono/date-utils'
import { useSplitEnabled } from '@buffer-mono/features'
import {
  UsersIcon,
  ChartColumnIcon,
  ChannelAvatar,
  CopyIcon,
  DropdownMenu,
  ExternalLinkIcon,
  EyeIcon,
  Flex,
  GlobeIcon,
  Heading,
  IconButton,
  Loader,
  PageHeader,
  PageLayout,
  SettingsIcon,
  TagIcon,
  TrendingUpIcon,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useShouldShowDeveloperData } from '~publish/components/DeveloperData'
import { FilterByTag } from '~publish/components/FilterByTag'
import { SaveViewButton } from '~publish/components/MainSidebar/components/SavedViewsSection'
import {
  useChannelsConnected,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { useAnalyzeSunsetEnabled } from '~publish/hooks/useAnalyzeSunsetEnabled'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import {
  POST_TYPE_FILTER_SPLIT,
  supportedPostTypesForService,
} from '~publish/helpers/postType'
import { canPerform } from '~publish/utils/permissions/canPerform'
import { isViewOnlyPublishChannel } from '~publish/utils/permissions/viewOnlyPublish'
import { usePostCounts } from '~publish/pages/AllChannels/PostList/usePostCounts'
import { TagFilterList } from '~publish/pages/Channel/TagFilterSubMenu'
import { PostingTimePreviewModal } from '~publish/pages/Channel/ChannelSettings/PostingSchedule/components/PostingTimePreviewModal'
import { countActiveSlots } from '~publish/pages/Channel/ChannelSettings/PostingSchedule/utils/countActiveSlots'

import type { SortDirection, TopPostsSortBy } from '../graphqlEnums'

import { InsightsHeaderActions } from './InsightsHeaderActions'
import { getTakeawayScheduleTarget } from './getTakeawayScheduleTarget'
import { useChannelBenchmarks } from './hooks/useChannelBenchmarks'
import { useAllChannelsPostInsights } from '../AllChannelsInsights/hooks/useAllChannelsPostInsights'
import {
  ComparisonToggle,
  DateRangeToggle,
  InsightsAccessDenied,
  InsightsNavigationTabs,
  InsightsPermissionBanner,
  InsightsPermissionBanner_Channel,
  MetricUnit,
  MetricsNarrativeChart,
  SummaryMode,
  SummaryMetrics,
  Takeaways,
  MAX_VISIBLE_TAKEAWAYS,
  buildFollowersSummaryMetric,
} from '../components'
import type { MetricSeries, SummaryMetricItem } from '../components'
import {
  AnnotationPopover,
  buildAnnotationAriaLabel,
  buildDayAlignedTicks,
  bucketEventDateGroups,
  groupChannelsByEventDate,
  pickAnnotationBucketUnit,
} from '../components/charts'
import type { ChartAnnotation } from '../components/charts'
import type { ChannelLegendData } from '../components/ChannelLegendItem'
import type { TakeawayItem } from '../components/Takeaways/types'
import { InsightsUpgradeDialog } from '../components/InsightsUpgradeDialog'
import { TopPosts } from '../components/TopPosts/TopPosts'
import {
  InsightsPageProvider,
  InsightsSnapshotProvider,
  useInsightsDateRange,
  useInsightsFilterInput,
  useInsightsPostTypes,
  useInsightsTags,
  useInsightsUpgradePath,
  useInsightsComparison,
} from '../context'
import {
  FilterByPostType,
  PostTypeFilterMenu,
} from '~publish/components/FilterByPostType'
import {
  postMetricColumns,
  useChannelTakeaways,
  useColumnVisibility,
  useRefreshChannelTakeaways,
  useTakeawaysGate,
  useTopPosts,
  useTrackTakeawaysViewed,
} from '../hooks'
import {
  comparisonTabLabel,
  comparisonTriggerLabel,
  getLinkedInBoostUrl,
  getPostTopic,
  isBelowFacebookFollowerThreshold,
  isChannelSupportedByInsights,
  isFollowersOnlyService,
  isPostsOnlyService,
  isPeriodLongerThanOneMonth,
  isPeriodLongerThanOneWeek,
  supportsBenchmarks,
  zeroFillTimeSeries,
} from '../utils'
import { getChannelReadDenial } from './getChannelReadDenial'
import { InsightsChannelNotFound } from './InsightsChannelNotFound'
import { InsightsNotSupported } from './InsightsNotSupported'
import { getTopPostsPageCursor } from './topPostsCursor'

import sectionButtonStyles from '../components/sectionActionButton.module.css'
import styles from './InsightsChannel.module.css'

const TOP_POSTS_PER_PAGE = 10

const ANALYZE_SUPPORTED_SERVICES = [
  { service: 'facebook', types: ['page'] },
  { service: 'instagram', types: ['business'] },
  { service: 'twitter', types: ['profile'] },
  { service: 'linkedin', types: ['page'] },
]

const GetInsightsChannelInfo = graphql(
  /* GraphQL */ `
    query GetInsightsChannelInfo($channelId: ChannelId!) {
      channel(input: { id: $channelId }) {
        id
        allowedActions
        name
        displayName
        service
        serviceId
        type
        timezone
        apiVersion
        avatar
        isDisconnected
        analytics {
          channelLastUpdatedAt
          postsLastUpdatedAt
        }
        postingGoal {
          goal
          sentCount
          scheduledCount
          periodStart
          periodEnd
          status
        }
        postingSchedule {
          day
          times
          paused
        }
        ...InsightsPermissionBanner_Channel
      }
    }
  `,
  [InsightsPermissionBanner_Channel],
)

// ========================================
// Entry point — thin wrapper that mounts the provider
// ========================================

export function InsightsChannel(): JSX.Element {
  const { channelId } = useParams<{ channelId: string }>()

  if (!channelId) {
    return <Navigate to="/" replace />
  }

  return (
    <InsightsPageProvider channelId={channelId}>
      <InsightsSnapshotProvider>
        <InsightsChannelContent channelId={channelId} />
      </InsightsSnapshotProvider>
    </InsightsPageProvider>
  )
}

// ========================================
// Page content — consumes the provider
// ========================================

function InsightsChannelContent({
  channelId,
}: {
  channelId: string
}): JSX.Element {
  const { search } = useLocation()
  // TODO: Add feature flag check before merging: useSplitEnabled('new-insights-page')
  const { isEnabled: isBoostEnabled } = useSplitEnabled(
    'geid-linkedin-boost-ctas',
  )
  const channels = useChannelsConnected()

  const organizationId = useOrganizationId() ?? ''
  const weekStartsOn: 0 | 1 = useWeekStartsOn() === 'monday' ? 1 : 0

  // Shared page concerns from context
  const {
    startDate,
    endDate,
    selectedDateRange,
    onDateRangeChange,
    onCustomRangeApply,
  } = useInsightsDateRange()
  const { selectedTagIds, tagFilter, setSelectedTagIds } = useInsightsTags()
  const {
    isPostTypeFilterEnabled,
    selectedPostTypes,
    setSelectedPostTypes,
    clearPostFilters,
  } = useInsightsPostTypes()
  const {
    shouldDisplayUpgradePath,
    isCustomDateUpgradeOpen,
    setIsCustomDateUpgradeOpen,
    handleCustomBlocked,
    handleCustomDateUpgradeClick,
  } = useInsightsUpgradePath()
  const {
    comparisonMode,
    comparisonStartDate,
    comparisonEndDate,
    onComparisonModeChange,
    onCustomComparisonApply,
  } = useInsightsComparison()

  const [topPostsPage, setTopPostsPage] = useState(1)
  const [topPostsSortBy, setTopPostsSortBy] =
    useState<TopPostsSortBy>('reactions')
  const [topPostsSortDirection, setTopPostsSortDirection] =
    useState<SortDirection>('desc')
  // Which period's posts are displayed in the Performance per Post section.
  // Switching tabs resets pagination but preserves sort.
  const [topPostsPeriod, setTopPostsPeriod] = useState<
    'primary' | 'comparison'
  >('primary')

  // Resolved comparison window driven by the ComparisonToggle (defaults to
  // the equal-length previous period). The custom-invalid edge case falls
  // back to previous-period via useComparisonSelector, so these are always
  // defined here.
  const comparisonRange = useMemo(() => {
    if (comparisonStartDate && comparisonEndDate) {
      return { startDate: comparisonStartDate, endDate: comparisonEndDate }
    }
    const { previousStartDate, previousEndDate } = getPreviousPeriodDateRange(
      startDate,
      endDate,
    )
    return { startDate: previousStartDate, endDate: previousEndDate }
  }, [comparisonStartDate, comparisonEndDate, startDate, endDate])

  // "Same Period Last Month" can't produce an equal-length window when the
  // selected period spans more than a month, so the option is disabled.
  const disableMonthlyComparison = isPeriodLongerThanOneMonth(
    startDate,
    endDate,
  )
  // "Same Period Last Week" only makes sense for ranges of ≤7 days.
  const disableWeeklyComparison = isPeriodLongerThanOneWeek(startDate, endDate)

  const channel = useMemo(() => {
    return channels.find((c) => c.id === channelId)
  }, [channels, channelId])

  // "Snapshot" networks (Bluesky, Twitter, Mastodon) report followers only:
  // skip fetching the (all-null) reach/views series and hide the Visibility
  // narrative (which plots views).
  const followersOnly = isFollowersOnlyService(channel?.service)
  // Substack reports no channel-level metrics, so its channel never syncs and
  // the follower-based narratives have nothing to plot.
  const postsOnly = isPostsOnlyService(channel?.service)

  const {
    data: channelData,
    loading: channelDataLoading,
    error: channelError,
  } = useQuery<ResultOf<typeof GetInsightsChannelInfo>>(
    GetInsightsChannelInfo,
    {
      variables: {
        channelId: channelId ?? '',
      },
      skip: !channelId,
    },
  )

  const channelWithGoal = channelData?.channel
  const canExport = canPerform(channelWithGoal, 'exportInsights')
  const canManageChannel = canPerform(channelWithGoal, 'manageUpdates')
  const isViewOnly = isViewOnlyPublishChannel(channelWithGoal)
  const hasNeverSynced =
    !postsOnly &&
    !channelDataLoading &&
    channelWithGoal != null &&
    channelWithGoal.analytics?.channelLastUpdatedAt == null

  // `channel(input)` gates the read on `ViewChannel` (any non-`none` access) and
  // throws when it fails, so a channel the actor can't reach comes back as a
  // coded GraphQL error and no channel — not a `null` channel. Classify it so
  // the branches below can show the right full-page state.
  const channelReadDenial = getChannelReadDenial(channelError)

  // The actor can't view insights for this channel — either the channel loaded
  // but grants no `viewInsights` (publish/comments both denied), or the channel
  // read itself was forbidden. Drives the Access Denied state below AND skips
  // every insights data hook so a denied channel never issues backend requests
  // that would 403. `viewInsights` is granted by publish OR comments access
  // (see channelActionsConfig).
  const isInsightsDenied =
    !channelDataLoading &&
    (channelReadDenial === 'forbidden' ||
      (channelWithGoal != null && !canPerform(channelWithGoal, 'viewInsights')))

  const canCreatePosts = canPerform(channelWithGoal, 'manageUpdates')

  // Determine if the Analyze link should be shown
  const isAnalyzeSunset = useAnalyzeSunsetEnabled()
  const shouldShowAnalyzeLink = useMemo(() => {
    // Analyze is retired for this customer — no entry point back into it.
    if (isAnalyzeSunset) return false

    const service = channelWithGoal?.service
    const serviceType = channelWithGoal?.type
    const apiVersion = channelWithGoal?.apiVersion

    if (!service || !serviceType) return false
    if (service === 'instagram' && apiVersion === 'instagramLogin') return false

    return ANALYZE_SUPPORTED_SERVICES.some(
      (supported) =>
        supported.service === service && supported.types.includes(serviceType),
    )
  }, [
    isAnalyzeSunset,
    channelWithGoal?.service,
    channelWithGoal?.type,
    channelWithGoal?.apiVersion,
  ])

  const supportedPostTypes = useMemo(
    () => supportedPostTypesForService(channelWithGoal?.service),
    [channelWithGoal?.service],
  )
  const showPostTypeFilter =
    isPostTypeFilterEnabled && supportedPostTypes.length > 0

  // Only formats this service can produce may reach a query. The control is
  // hidden for a service that supports none, so a `postTypes` URL on one would
  // otherwise narrow the page with nothing on screen to clear it.
  const effectivePostTypes = useMemo(() => {
    const supported: readonly string[] = supportedPostTypes
    return selectedPostTypes.filter((postType) => supported.includes(postType))
  }, [selectedPostTypes, supportedPostTypes])

  const {
    data: allChannelsData,
    loading: postInsightsLoading,
    error: postInsightsError,
  } = useAllChannelsPostInsights({
    organizationId,
    startDate,
    endDate,
    channelIds: [channelId],
    tags: tagFilter,
    postTypes: effectivePostTypes,
    comparisonStartDate,
    comparisonEndDate,
    skip: isInsightsDenied,
  })

  // When viewing the comparison tab, list posts from the comparison window
  // instead of the primary one. Null if comparison dates somehow aren't set
  // yet (shouldn't happen — the toggle is hidden in that case).
  //
  // The window travels to the query as a comparison rather than being swapped
  // into the filter's primary range, which is what the `topPosts` query used to
  // receive. The free-plan history cap is measured against the primary range,
  // so disguising a comparison as one rejected every free organization's
  // default "Previous Period" tab — its window starts ~60 days back on the
  // default 30-day range, past the 31-day cap — even though the charts and
  // deltas on this same page show that window's data ungated (CORE-7746).
  const topPostsComparison = useMemo(
    () =>
      topPostsPeriod === 'comparison' &&
      comparisonStartDate &&
      comparisonEndDate
        ? { startDate: comparisonStartDate, endDate: comparisonEndDate }
        : null,
    [topPostsPeriod, comparisonStartDate, comparisonEndDate],
  )

  // The window whose posts are on screen — the comparison when that tab is
  // active, the primary otherwise. Identity-stable, so it can drive the
  // pagination reset below.
  const topPostsDates = useMemo(
    () => topPostsComparison ?? { startDate, endDate },
    [topPostsComparison, startDate, endDate],
  )

  // Filter input for the top-posts query. Always the primary range: see
  // `topPostsComparison` above for why the comparison window must not be
  // swapped in here.
  const insightsFilter = useInsightsFilterInput({
    startDate,
    endDate,
    postTypes: effectivePostTypes,
  })

  const {
    topPosts,
    totalCount: topPostsTotalCount,
    loading: topPostsLoading,
    error: topPostsError,
  } = useTopPosts({
    filter: insightsFilter,
    sort: { field: topPostsSortBy, direction: topPostsSortDirection },
    comparisonStartDate: topPostsComparison?.startDate,
    comparisonEndDate: topPostsComparison?.endDate,
    first: TOP_POSTS_PER_PAGE,
    // The Top Posts cursor encodes an absolute offset, so the `after` for any
    // page is derived directly from the page number — no need to observe the
    // previous page's response first. This lets the user jump to a non-adjacent
    // page and is immune to sort/cache timing (CORE-6989).
    after: getTopPostsPageCursor(topPostsPage, TOP_POSTS_PER_PAGE),
    skip: isInsightsDenied,
  })

  // Read the Top Posts table's column selection so the export reflects it.
  // Second `useColumnVisibility` instance for the same scope as the table's
  // (TopPosts) — popcorn's useLocalStorage broadcasts on write, so both stay
  // in sync within the tab. availableMetrics is computed exactly as TopPosts
  // does so the preference resolves identically.
  const postMetrics = useMemo(() => postMetricColumns(topPosts), [topPosts])
  const { visibleMetrics: postColumns } = useColumnVisibility({
    availableMetrics: postMetrics,
    scopeId: channelId ?? '',
  })

  // Reset pagination when the **effective** posts window changes.
  // `topPostsDates` is memoized so its identity is stable until either the
  // tab toggle, the primary range, or the comparison range changes.
  // `effectivePostTypes` belongs here too: a Top Posts cursor encodes an
  // absolute offset, so a cursor taken under one filter points at a different
  // row once the filter changes.
  useEffect(() => {
    setTopPostsPage(1)
  }, [topPostsDates, selectedTagIds, effectivePostTypes, channelId])

  const metrics = useMemo(
    () => allChannelsData?.channelInsights?.[0]?.metrics ?? [],
    [allChannelsData],
  )
  const channelTimeSeries = allChannelsData?.channelTimeSeries?.[0]
  const timeSeries = useMemo(
    () => ({
      followers: channelTimeSeries?.primary.followers ?? [],
      reach: channelTimeSeries?.primary.reach ?? [],
      views: channelTimeSeries?.primary.views ?? [],
      posts: channelTimeSeries?.primary.posts ?? [],
    }),
    [channelTimeSeries],
  )
  const comparisonTimeSeries = useMemo(
    () =>
      channelTimeSeries?.comparison
        ? {
            followers: channelTimeSeries.comparison.followers,
            reach: channelTimeSeries.comparison.reach,
            views: channelTimeSeries.comparison.views,
            posts: channelTimeSeries.comparison.posts,
          }
        : undefined,
    [channelTimeSeries],
  )

  // Surface the follower count first in the Summary — it's the point-in-time
  // number customers ask for (CORE-6157). Derived from the same followers
  // series the chart uses; omitted for channels with no follower readings.
  const summaryMetrics = useMemo<SummaryMetricItem[]>(() => {
    const followersMetric = buildFollowersSummaryMetric({
      primary: timeSeries.followers,
      comparison: comparisonTimeSeries?.followers,
    })
    return followersMetric ? [followersMetric, ...metrics] : metrics
  }, [timeSeries.followers, comparisonTimeSeries, metrics])

  // Only show skeleton on initial load, not on refetch
  const hasPostInsightsData = metrics.length > 0
  const postInsightsRefreshing = postInsightsLoading && hasPostInsightsData

  // A Facebook Page under Meta's follower threshold has no channel-level
  // time-series (reach/views) by design. Passed to the metrics chart so its
  // no-data state explains the threshold instead of the generic notice; the
  // summary and post-level sections are unaffected. A range with no follower
  // reading returns false and falls back to the generic chart state.
  // @see CORE-6425
  const isBelowFollowerThreshold = isBelowFacebookFollowerThreshold({
    service: channelWithGoal?.service,
    channelType: channelWithGoal?.type,
    followers: timeSeries.followers,
  })

  // No posts published in the period → the channel is inactive, so the metrics
  // chart nudges toward Takeaways instead of reading a flat metric as "held
  // steady". @see CORE-6444
  const isInactive = useMemo(
    () => !timeSeries.posts.some((p) => (p.value ?? 0) > 0),
    [timeSeries.posts],
  )

  const takeawaysRef = useRef<HTMLDivElement>(null)
  const handleReviewTakeaways = useCallback(() => {
    takeawaysRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  // Only a period reaching into the past week is actionable now — an older
  // window may read 0 today but not when it was current — so the inactive
  // prompt links to Takeaways only for a current period. @see CORE-6444
  const isCurrentPeriod = useMemo(() => {
    const oneWeekAgo = new Date()
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
    return endDate.getTime() >= oneWeekAgo.getTime()
  }, [endDate])

  const { counts, loading: postCountsLoading } = usePostCounts({
    // Withhold the org id on a denied read so the query skips (the hook skips
    // when organizationId is absent) — `counts` isn't rendered once we
    // short-circuit to the Access Denied state.
    organizationId: isInsightsDenied ? undefined : organizationId,
    channelIds: [channelId],
  })
  const hasQueuedPosts = !postCountsLoading && (counts?.queue ?? 0) > 0

  const takeawaysGate = useTakeawaysGate({
    organizationId: isInsightsDenied ? undefined : organizationId,
    channelIds: [channelId],
  })

  const { data: takeaways, loading: takeawaysLoading } = useChannelTakeaways({
    channelId,
    organizationId,
    skip: isInsightsDenied || !takeawaysGate.shouldRequestTakeaways,
  })
  const { refresh: refreshTakeaways, loading: takeawaysRefreshing } =
    useRefreshChannelTakeaways({
      channelId,
      organizationId,
    })

  const shouldShowDeveloperData = useShouldShowDeveloperData()

  const [showPostingSchedulePreviewModal, setShowPostingSchedulePreviewModal] =
    useState(false)

  const handleOpenRecommendedTimesDialog = useCallback(
    () => setShowPostingSchedulePreviewModal(true),
    [],
  )

  // The "Adjust your schedule" Takeaway is surfaced when the channel is
  // under-scheduled, so expand (add mode) rather than overriding the
  // customer's existing slots.
  const activeSlotCount = countActiveSlots(channelWithGoal?.postingSchedule)
  const recommendedTimesPostsPerWeek = getTakeawayScheduleTarget({
    activeSlotCount,
    postingGoal: channelWithGoal?.postingGoal?.goal,
  })

  const handleBoostCtaClick = useCallback(() => {
    BufferTracker.cTAClicked({
      clientName: 'publishWeb',
      cta: 'publish-insights-takeaways-boostLinkedIn-1',
      upgradePathName: null,
      organizationId: organizationId ?? undefined,
      product: 'publish',
    })
  }, [organizationId])

  // LinkedIn boost takeaway: inject a frontend-generated card for LinkedIn channels
  // with at least 5 posts in the selected period.
  const linkedInBoostTakeaway = useMemo((): TakeawayItem | null => {
    if (!isBoostEnabled) return null
    if (channelWithGoal?.service !== 'linkedin') return null
    if (topPostsTotalCount < 5) return null

    const topPost = topPosts[0]
    const boostUrl = getLinkedInBoostUrl(
      topPost?.externalLink,
      channelWithGoal?.serviceId,
    )
    if (!boostUrl) return null

    const topic = getPostTopic(topPost?.text)
    // "Highly performing" heuristic: top post has notably more reactions than the runner-up
    const topReactions =
      topPost?.metrics?.find((m) => m.type === 'reactions')?.value ?? 0
    const runnerUpReactions =
      topPosts[1]?.metrics?.find((m) => m.type === 'reactions')?.value ?? 0
    const isHighlyPerforming =
      topReactions > 0 && topReactions >= runnerUpReactions * 2

    return {
      title: 'Boost Your Post for Greater Reach',
      description: isHighlyPerforming
        ? `Your post about "${topic}" is blowing up! Consider boosting it to reach even more people.`
        : `Your post about "${topic}" could resonate with a broader audience. Consider boosting it to reach even more people.`,
      category: 'boostLinkedIn',
      channelIds: [channelId],
      ctaLabel: 'Boost post on LinkedIn',
      ctaUrl: boostUrl,
      onCtaClick: handleBoostCtaClick,
    }
  }, [
    isBoostEnabled,
    channelId,
    channelWithGoal?.service,
    channelWithGoal?.serviceId,
    topPostsTotalCount,
    topPosts,
    handleBoostCtaClick,
  ])

  const mergedTakeaways = useMemo(() => {
    if (!linkedInBoostTakeaway) return takeaways
    return [linkedInBoostTakeaway, ...takeaways]
  }, [takeaways, linkedInBoostTakeaway])

  // Final rendered list: capped at the same limit <Takeaways /> renders, so the
  // tracked impression list cannot drift from what the user actually sees.
  const visibleTakeaways = useMemo(
    () => mergedTakeaways.slice(0, MAX_VISIBLE_TAKEAWAYS),
    [mergedTakeaways],
  )

  useTrackTakeawaysViewed({
    channelId,
    organizationId,
    loading: takeawaysLoading,
    visibleTakeaways,
  })

  const chartMetrics = useMemo(
    () =>
      [
        {
          key: 'followers',
          title: 'Followers',
          icon: <UsersIcon size="small" />,
          data: timeSeries.followers,
          comparisonData: comparisonTimeSeries?.followers,
          color: 'var(--chart-color-followers)',
        },
        {
          key: 'reach',
          title: 'Reach',
          icon: <GlobeIcon size="small" />,
          data: timeSeries.reach,
          comparisonData: comparisonTimeSeries?.reach,
          color: 'var(--chart-color-reach)',
          summaryMode: SummaryMode.Total,
        },
        {
          key: 'views',
          title: 'Profile Views',
          icon: <EyeIcon size="small" />,
          data: timeSeries.views,
          comparisonData: comparisonTimeSeries?.views,
          color: 'var(--chart-color-views)',
          summaryMode: SummaryMode.Total,
        },
        {
          key: 'posts',
          title: 'Posts',
          icon: <CopyIcon size="small" />,
          data:
            timeSeries.posts.length > 0
              ? zeroFillTimeSeries(timeSeries.posts, startDate, endDate)
              : timeSeries.posts,
          comparisonData: comparisonTimeSeries?.posts,
          color: 'var(--chart-color-posts)',
          summaryMode: SummaryMode.Total,
        },
      ].filter((m) =>
        m.data.some(
          (p: { value: number | null | undefined }) => p.value != null,
        ),
      ),
    [timeSeries, comparisonTimeSeries, startDate, endDate],
  )

  // Flag the day this channel's data collection began on the over-time chart.
  // `groupChannelsByEventDate` drops dates before the range, so the flag shows
  // only for a line that begins mid-chart. One channel → no data before it (hatch on).
  const dataCollectionAnnotations = useMemo<ChartAnnotation[]>(() => {
    const legendChannel: ChannelLegendData = {
      id: channelId,
      name: channelWithGoal?.displayName ?? channel?.name ?? 'This channel',
      service: channel?.service ?? channelWithGoal?.service ?? '',
      color: 'var(--chart-color-followers)',
    }
    const eventDateById = new Map([
      [channelId, channelTimeSeries?.firstChannelDataCollectedAt],
    ])
    const groups = groupChannelsByEventDate(
      [legendChannel],
      eventDateById,
      startDate,
      endDate,
    )
    const unit = pickAnnotationBucketUnit(startDate, endDate)
    return bucketEventDateGroups(groups, unit).map((bucket) => ({
      id: bucket.id,
      date: new Date(bucket.positionTimestamp),
      lineDates: bucket.dayTimestamps.map((ts) => new Date(ts)),
      showNoDataOverlay: true,
      ariaLabel: buildAnnotationAriaLabel(bucket, 'started collecting data'),
      content: (
        <AnnotationPopover>
          {bucket.days.map((day) => (
            <AnnotationPopover.DateGroup key={day.id} date={day.eventDate}>
              {day.channels.map((ch) => (
                <AnnotationPopover.ChannelRow key={ch.id} channel={ch}>
                  started collecting data
                </AnnotationPopover.ChannelRow>
              ))}
            </AnnotationPopover.DateGroup>
          ))}
        </AnnotationPopover>
      ),
    }))
  }, [
    channelId,
    channelWithGoal?.displayName,
    channelWithGoal?.service,
    channel?.name,
    channel?.service,
    channelTimeSeries?.firstChannelDataCollectedAt,
    startDate,
    endDate,
  ])

  // Align ticks to days so labels sit under the flag; default layout otherwise.
  const dataCollectionTicks = useMemo(
    () =>
      dataCollectionAnnotations.length > 0
        ? buildDayAlignedTicks(startDate, endDate)
        : undefined,
    [dataCollectionAnnotations.length, startDate, endDate],
  )

  // Benchmark data: follower growth rate vs comparable channels on the same
  // network. Only fetched for networks that have a benchmark definition.
  const hasBenchmarks = supportsBenchmarks(channelWithGoal?.service)

  // Benchmark always shows the last 12 months of settled data, independent of
  // the date range selector — "Date filters don't apply to this chart."
  // Compute fresh on each render so the 12-month window updates daily at midnight.
  // The cache key changes only when dates actually differ, so this is efficient.
  const benchmarkEndDate = new Date()
  const benchmarkStartDate = ((): Date => {
    const d = new Date()
    d.setFullYear(d.getFullYear() - 1)
    return d
  })()

  const { series: benchmarkSeries } = useChannelBenchmarks({
    organizationId: organizationId ?? '',
    channelId,
    startDate: benchmarkStartDate,
    endDate: benchmarkEndDate,
    skip: !hasBenchmarks || isInsightsDenied,
  })

  // Latest percentile rank: most recent settled month's rank (0–100).
  const benchmarkPercentileRank = useMemo(
    () => benchmarkSeries[0]?.history.at(-1)?.channelPercentileRank ?? null,
    [benchmarkSeries],
  )

  const benchmarkMetrics = useMemo(() => {
    if (!hasBenchmarks) return []
    const growth = benchmarkSeries[0]
    if (!growth?.history.length) return []

    const toDateStr = (month: string): string => {
      const d = new Date(month)
      if (isNaN(d.getTime())) return '' // Invalid date format from API
      return d.toISOString().slice(0, 10)
    }

    // API stores growth rates as decimals (e.g. 0.008 = 0.8%) — multiply by 100.
    const toPercent = (v: number | null | undefined): number | null =>
      v != null ? v * 100 : null

    const series: MetricSeries[] = [
      {
        key: 'benchmark-growth-rate',
        title: 'Growth Rate',
        icon: <TrendingUpIcon size="small" />,
        color: 'var(--chart-color-benchmark-channel)',
        summaryMode: SummaryMode.Latest,
        unit: MetricUnit.Percentage,
        data: growth.history.map((h) => ({
          date: toDateStr(h.month),
          value: toPercent(h.channelValue),
        })),
      },
      {
        key: 'benchmark-median',
        title: 'Median',
        icon: <ChartColumnIcon size="small" />,
        color: 'var(--chart-color-benchmark-median)',
        summaryMode: SummaryMode.Latest,
        unit: MetricUnit.Percentage,
        // Dashed so it reads as a reference line rather than a primary series.
        dashed: true,
        data: growth.history.map((h) => ({
          date: toDateStr(h.month),
          value: toPercent(h.comparison?.percentiles.p50),
        })),
      },
    ]
    return series.filter((m) => m.data.some((p) => p.value != null))
  }, [benchmarkSeries, hasBenchmarks])

  // Combine once, memoised: a fresh `[...chartMetrics, ...benchmarkMetrics]` on
  // every render would give MetricsNarrativeChart a new `metrics` reference each
  // time, busting its series memo and restarting the chart's line entry
  // animation (recharts re-animates whenever the points reference changes).
  const narrativeMetrics = useMemo(
    () => [...chartMetrics, ...benchmarkMetrics],
    [chartMetrics, benchmarkMetrics],
  )

  // The id doesn't resolve to a channel for this actor: either a loaded query
  // returned an explicit `null` channel, or the read threw NOT_FOUND. Show a
  // not-found state rather than the full shell with empty data. A transient /
  // transport failure carries no code, so it isn't mislabelled as "not found".
  if (
    !channelDataLoading &&
    (channelWithGoal === null || channelReadDenial === 'notFound')
  ) {
    return <InsightsChannelNotFound />
  }

  const isChannelUnsupportedForInsights =
    !!channelWithGoal &&
    !isChannelSupportedByInsights(channelWithGoal.service, channelWithGoal.type)

  if (
    !channelDataLoading &&
    channelWithGoal &&
    isChannelUnsupportedForInsights
  ) {
    return (
      <InsightsNotSupported
        channel={channelWithGoal}
        avatar={channel?.avatar ?? undefined}
      />
    )
  }

  // The actor can't view this channel's insights — a forbidden read, or a
  // loaded channel without `viewInsights`. Show an explicit Access Denied state
  // rather than empty "no data" charts. A forbidden read returns no channel
  // object, so fall back to the generic channel-less copy. The insights data
  // hooks above are skipped on this path (see `isInsightsDenied`).
  if (isInsightsDenied) {
    return (
      <InsightsAccessDenied
        channel={
          channelWithGoal
            ? {
                service: channelWithGoal.service,
                name: getChannelNameForDisplay(channelWithGoal),
                avatar: channelWithGoal.avatar ?? channel?.avatar ?? undefined,
              }
            : undefined
        }
      />
    )
  }

  return (
    <>
      <Outlet />
      <PageLayout>
        <PageHeader separator={false}>
          <PageHeader.Row>
            <PageHeader.TitleGroup
              leadingGap="comfortable"
              data-testid="insights-page-title"
            >
              {channel && (
                <PageHeader.TitleLeading>
                  <ChannelAvatar
                    src={channel.avatar}
                    alt={channel.service}
                    channel={channel.service}
                    size="mid"
                    className={styles.channelAvatar}
                  />
                </PageHeader.TitleLeading>
              )}
              <PageHeader.Title>
                {channelWithGoal ? (
                  <span translate="no">
                    {getChannelNameForDisplay(channelWithGoal)}
                  </span>
                ) : channel?.name ? (
                  <span translate="no">{channel.name}</span>
                ) : (
                  'Insights'
                )}
              </PageHeader.Title>
              <PageHeader.TitleAdornments>
                <SaveViewButton />
                {canManageChannel && (
                  <IconButton
                    id="settings-menu"
                    variant="tertiary"
                    label="Channel settings"
                    tooltip="Channel Settings"
                    size="small"
                    as={Link}
                    to={`/channels/${channelId}/settings`}
                  >
                    <SettingsIcon />
                  </IconButton>
                )}
              </PageHeader.TitleAdornments>
            </PageHeader.TitleGroup>
            <PageHeader.Actions>
              <InsightsHeaderActions
                isViewOnly={isViewOnly}
                shouldShowAnalyzeLink={shouldShowAnalyzeLink}
                canExport={canExport}
                analyzeUrl={
                  channelWithGoal?.service
                    ? `https://analyze.buffer.com/${channelWithGoal.service}/overview/${channelId}`
                    : undefined
                }
                postCount={topPostsTotalCount}
                postSort={{
                  field: topPostsSortBy,
                  direction: topPostsSortDirection,
                }}
                postColumns={postColumns.map((metric) => metric.type)}
                postTypes={effectivePostTypes}
                channelLastUpdatedAt={
                  channelWithGoal?.analytics?.channelLastUpdatedAt
                }
                postsLastUpdatedAt={
                  channelWithGoal?.analytics?.postsLastUpdatedAt
                }
                developerData={
                  shouldShowDeveloperData
                    ? {
                        kind: 'channel',
                        channelId: channelId ?? '',
                        serviceId: channelWithGoal?.serviceId,
                        takeawaysLoading,
                        takeawaysRefreshing,
                        onRefreshTakeaways: refreshTakeaways,
                      }
                    : undefined
                }
              />
            </PageHeader.Actions>
          </PageHeader.Row>
          <InsightsNavigationTabs
            activeTab="post-insights"
            postInsightsHref={`/insights/${channelId}${search}`}
          />
        </PageHeader>

        <PageLayout.Container className={styles.contentContainer}>
          {channelDataLoading && (
            <Flex justify="center" align="center" fullWidth fullHeight>
              <Loader />
            </Flex>
          )}
          {!channelDataLoading && (
            <Flex direction="column" gap="lg">
              {channelWithGoal && (
                <InsightsPermissionBanner channel={channelWithGoal} />
              )}
              <div ref={takeawaysRef} className={styles.takeawaysWrapper}>
                <Takeaways
                  gate={takeawaysGate}
                  channelId={channelId}
                  channelService={channelWithGoal?.service}
                  canCreatePosts={canCreatePosts}
                  onOpenRecommendedTimesDialog={
                    handleOpenRecommendedTimesDialog
                  }
                  takeaways={visibleTakeaways}
                  loading={takeawaysLoading && visibleTakeaways.length === 0}
                  refreshing={
                    (takeawaysLoading && visibleTakeaways.length > 0) ||
                    takeawaysRefreshing
                  }
                />
              </div>
              {channelWithGoal && !postCountsLoading && (
                <PostingTimePreviewModal
                  open={showPostingSchedulePreviewModal}
                  onOpenChange={setShowPostingSchedulePreviewModal}
                  channel={channelWithGoal}
                  strategy={{
                    optimize: {
                      updateMode: 'add',
                      postsPerWeek: recommendedTimesPostsPerWeek,
                    },
                  }}
                  hasQueuedPosts={hasQueuedPosts}
                />
              )}
              <Heading as="h2" size="lg" className={styles.allInsightsHeading}>
                All Insights
              </Heading>
              {/* Only the filter row sticks; the heading above scrolls away.
                    Sticky must be a direct child of this tall column so it stays
                    pinned across all the sections below it. */}
              <Flex
                direction="row"
                align="center"
                gap="xs"
                fullWidth
                className={styles.stickyFilters}
              >
                <DateRangeToggle
                  selectedDateRange={selectedDateRange}
                  onDateRangeChange={onDateRangeChange}
                  currentRange={{ startDate, endDate }}
                  onCustomRangeApply={onCustomRangeApply}
                  weekStartsOn={weekStartsOn}
                  showUpgradePath={shouldDisplayUpgradePath}
                  onCustomBlocked={handleCustomBlocked}
                />
                {!shouldDisplayUpgradePath && (
                  <ComparisonToggle
                    comparisonMode={comparisonMode}
                    comparisonStartDate={comparisonStartDate}
                    comparisonEndDate={comparisonEndDate}
                    onComparisonModeChange={onComparisonModeChange}
                    onCustomComparisonApply={onCustomComparisonApply}
                    primaryStartDate={startDate}
                    primaryEndDate={endDate}
                    primaryRangePreset={selectedDateRange}
                    weekStartsOn={weekStartsOn}
                    disableMonthlyComparison={disableMonthlyComparison}
                    disableWeeklyComparison={disableWeeklyComparison}
                  />
                )}
                {/* Default (labelled, tertiary) Combobox trigger on wide
                      screens. Once the row runs out of room, a compact icon-only
                      trigger opens each list in a DropdownMenu, which becomes
                      a bottom-sheet drawer on small viewports — matching the
                      date-range, comparison, and All-Channels filter controls. */}
                <span className={styles.filters}>
                  <span className={styles.filterWide}>
                    <FilterByTag
                      value={selectedTagIds}
                      onSelect={setSelectedTagIds}
                    />
                  </span>
                  <span className={styles.filterNarrow}>
                    <DropdownMenu>
                      <DropdownMenu.Trigger>
                        <IconButton
                          label="Filter by tags"
                          variant="secondary"
                          size="medium"
                        >
                          <TagIcon />
                        </IconButton>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content align="end">
                        <TagFilterList
                          selectedTags={selectedTagIds}
                          onTagSelect={setSelectedTagIds}
                        />
                      </DropdownMenu.Content>
                    </DropdownMenu>
                  </span>
                  {showPostTypeFilter && (
                    <>
                      <span className={styles.filterWide}>
                        <FilterByPostType
                          options={supportedPostTypes}
                          value={effectivePostTypes}
                          onSelect={setSelectedPostTypes}
                          splitName={POST_TYPE_FILTER_SPLIT}
                        />
                      </span>
                      <span className={styles.filterNarrow}>
                        <PostTypeFilterMenu
                          options={supportedPostTypes}
                          value={effectivePostTypes}
                          onSelect={setSelectedPostTypes}
                        />
                      </span>
                    </>
                  )}
                </span>
              </Flex>
              <SummaryMetrics
                headingAs="h3"
                metrics={summaryMetrics}
                loading={postInsightsLoading && !hasPostInsightsData}
                refreshing={postInsightsRefreshing}
                error={postInsightsError}
                comparisonRange={comparisonRange}
                comparisonMode={comparisonMode}
                startDate={startDate}
                endDate={endDate}
                hasNeverSynced={hasNeverSynced}
                selectedDateRange={selectedDateRange}
                canCreatePosts={canCreatePosts}
                shareContext={
                  channel
                    ? {
                        kind: 'channel',
                        channelId,
                        channelName:
                          channelWithGoal?.displayName ??
                          channel.name ??
                          'this channel',
                        channelService: channel.service,
                        channelAvatarUrl:
                          channelWithGoal?.avatar ?? channel.avatar,
                      }
                    : undefined
                }
                headerActions={
                  <IconButton
                    className={sectionButtonStyles.neutralBackground}
                    size="medium"
                    as={Link}
                    to={`/channels/${channelId}/schedule?tab=sent`}
                    label="View All Sent Posts"
                    tooltip="View All Sent Posts"
                  >
                    <ExternalLinkIcon />
                  </IconButton>
                }
              />
              <TopPosts
                channelId={channelId}
                canCreatePosts={canCreatePosts}
                topPosts={topPosts}
                loading={topPostsLoading && topPosts.length === 0}
                refreshing={topPostsLoading && topPosts.length > 0}
                error={topPostsError}
                page={topPostsPage}
                perPage={TOP_POSTS_PER_PAGE}
                totalCount={topPostsTotalCount}
                onPageChange={setTopPostsPage}
                sortBy={topPostsSortBy}
                sortDirection={topPostsSortDirection}
                onSortChange={(field, direction) => {
                  setTopPostsSortBy(field)
                  setTopPostsSortDirection(direction)
                  setTopPostsPage(1)
                }}
                startDate={startDate}
                endDate={endDate}
                comparisonStartDate={comparisonStartDate}
                comparisonEndDate={comparisonEndDate}
                selectedDateRange={selectedDateRange}
                hasNeverSynced={hasNeverSynced}
                hasTagFilter={selectedTagIds.length > 0}
                onClearTagFilter={(): void => setSelectedTagIds([])}
                hasPostTypeFilter={effectivePostTypes.length > 0}
                onClearPostTypeFilter={(): void => setSelectedPostTypes([])}
                onClearAllFilters={clearPostFilters}
                period={topPostsPeriod}
                onPeriodChange={setTopPostsPeriod}
                comparisonLabel={
                  comparisonStartDate && comparisonEndDate
                    ? comparisonTabLabel(comparisonMode)
                    : undefined
                }
                comparisonLabelShort={
                  comparisonStartDate && comparisonEndDate
                    ? comparisonTriggerLabel(comparisonMode)
                    : undefined
                }
              />
              <MetricsNarrativeChart
                metrics={narrativeMetrics}
                canCreatePosts={canCreatePosts}
                loading={postInsightsLoading && !hasPostInsightsData}
                refreshing={postInsightsRefreshing}
                error={postInsightsError}
                channelInfo={{
                  channelIds: [channelId],
                  channelName: channelWithGoal?.displayName ?? channel?.name,
                  channelService: channel?.service,
                  channelAvatarUrl: channelWithGoal?.avatar ?? channel?.avatar,
                }}
                hasNeverSynced={hasNeverSynced}
                belowFollowerThreshold={isBelowFollowerThreshold}
                isInactive={isInactive}
                onReviewTakeaways={handleReviewTakeaways}
                isCurrentPeriod={isCurrentPeriod}
                supportsViews={!followersOnly}
                supportsFollowers={!postsOnly}
                benchmarkStartDate={benchmarkStartDate}
                benchmarkEndDate={benchmarkEndDate}
                benchmarkPercentileRank={benchmarkPercentileRank}
                hasTagFilter={selectedTagIds.length > 0}
                hasPostTypeFilter={effectivePostTypes.length > 0}
                annotations={dataCollectionAnnotations}
                xAxisTicks={dataCollectionTicks}
              />
            </Flex>
          )}
        </PageLayout.Container>
      </PageLayout>
      <InsightsUpgradeDialog
        open={isCustomDateUpgradeOpen}
        onOpenChange={setIsCustomDateUpgradeOpen}
        onUpgradeClick={handleCustomDateUpgradeClick}
      />
    </>
  )
}
